/**
 * @jsx jsx
 * @jsxRuntime classic
 * @jsxFrag React.Fragment
 */

import React, { useEffect, useState } from 'react';

import { jsx, cssMap } from '@compiled/react';
import { injectIntl, type IntlShape, FormattedMessage } from 'react-intl';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import Heading from '@atlaskit/heading/heading';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import TRELLO_ERROR_SRC from '../../assets/trello-error';
import UNVERIFIED_ERROR_SRC from '../../assets/unverified-error';
import {
	NAVIGATION_CHANNEL,
	OPERATIONAL_EVENT_TYPE,
	withAnalyticsEvents,
} from '../../common/utils/analytics';
import { errorToReason, type Reason } from '../../common/utils/error-to-reason';
import { FETCH_ERROR_NAME } from '../../common/utils/errors/fetch-error';
import { SwitcherLoadError } from '../../common/utils/errors/switcher-load-error';
import messages from '../../common/utils/messages';

const TRIGGER_SUBJECT = 'errorBoundary';
const ACTION_SUBJECT = 'rendered';

const styles = cssMap({
	robotsImg: {
		height: '200px',
	},
	trelloErrorImg: {
		width: '140px',
		height: '88px',
	},
	unverifiedUserImg: {
		height: '150px',
	},
	errorBoundaryBox: {
		display: 'flex',
		flex: 1,
		width: '343px',
		boxSizing: 'border-box',
		justifyContent: 'center',
		flexDirection: 'column',
		marginTop: token('space.600'),
		marginBottom: token('space.600'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	errorContent: {
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'column',
		textAlign: 'center',
	},
	errorHeading: {
		marginTop: token('space.400'),
	},
	errorMessage: {
		marginTop: token('space.300'),
	},
});

interface ErrorBoundaryProps {
	children: React.ReactNode;
	extraAnalyticProps?: object;
	hideFallbackUI?: boolean;
	intl: IntlShape;
	onErrorCallback?: (error: Error) => void;
	product?: string;
	triggerSubject?: string;
}

type ErrorBoundaryState = {
	hasError: boolean;
	reason?: Reason;
};

const ErrorMessage = (props: { children: React.ReactNode }) => (
	<Box xcss={styles.errorMessage}>
		<Text as="p">{props.children}</Text>
	</Box>
);

class ErrorBoundary extends React.Component<
	ErrorBoundaryProps & WithAnalyticsEventsProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = { hasError: false };

	fireOperationalEvent = (payload: AnalyticsEventPayload) => {
		if (this.props.createAnalyticsEvent) {
			this.props
				.createAnalyticsEvent({
					eventType: OPERATIONAL_EVENT_TYPE,
					actionSubject: this.props.triggerSubject || TRIGGER_SUBJECT,
					...payload,
				})
				.fire(NAVIGATION_CHANNEL);
		}
	};

	componentDidCatch(error: any) {
		const reason = error instanceof SwitcherLoadError ? error.reason() : errorToReason(error);

		this.setState(
			{
				hasError: true,
				reason,
			},
			() => {
				this.fireOperationalEvent({
					action: ACTION_SUBJECT,
					attributes: {
						...reason,
						...this.props.extraAnalyticProps,
					},
				});
			},
		);

		this.props.onErrorCallback?.(error);
	}

	renderErrorBody() {
		const { reason } = this.state;

		if (!reason) {
			return;
		}

		if (reason.name === FETCH_ERROR_NAME) {
			if (reason.status === 403) {
				return (
					<ErrorMessage>
						<FormattedMessage
							{...(fg('product-terminology-refresh')
								? messages.errorTextUnverifiedAppify
								: messages.errorTextUnverified)}
						/>
					</ErrorMessage>
				);
			}

			return (
				<ErrorMessage>
					<FormattedMessage {...messages.errorTextNetwork} />
				</ErrorMessage>
			);
		}

		return (
			<ErrorMessage>
				<FormattedMessage {...messages.errorText} />
			</ErrorMessage>
		);
	}

	renderErrorHeading() {
		const { reason } = this.state;

		if (!reason) {
			return;
		}

		if (reason.name === FETCH_ERROR_NAME && reason.status === 401) {
			return <FormattedMessage {...messages.errorHeadingLoggedOut} />;
		}

		if (reason.name === FETCH_ERROR_NAME && reason.status === 403) {
			return <FormattedMessage {...messages.errorHeadingUnverified} />;
		}

		return <FormattedMessage {...messages.errorHeading} />;
	}

	renderErrorImage() {
		const { intl, product } = this.props;
		const { reason } = this.state;

		if (product === 'trello') {
			return (
				<img
					css={styles.trelloErrorImg}
					src={TRELLO_ERROR_SRC}
					alt={intl.formatMessage(messages.errorImageAltText)}
				/>
			);
		}

		if (reason!.status === 403) {
			return (
				<img
					css={styles.unverifiedUserImg}
					src={UNVERIFIED_ERROR_SRC}
					alt={intl.formatMessage(messages.errorImageAltText)}
				/>
			);
		}

		return (
			<RobotImage css={styles.robotsImg} alt={intl.formatMessage(messages.errorImageAltText)} />
		);
	}

	render() {
		const { hideFallbackUI, children } = this.props;
		const { hasError } = this.state;

		if (hasError && hideFallbackUI) {
			return <></>;
		}

		if (hasError) {
			return (
				<div css={styles.errorBoundaryBox} data-testid="error-boundary">
					<div css={styles.errorContent}>
						{this.renderErrorImage()}
						<Box xcss={styles.errorHeading}>
							<Heading size="medium">{this.renderErrorHeading()}</Heading>
						</Box>
						{this.renderErrorBody()}
					</div>
				</div>
			);
		}

		return children;
	}
}

const RobotImage: React.ComponentType<React.ComponentProps<'img'>> = (props) => {
	const [imgSrc, setImgSrc] = useState<string | undefined>();
	useEffect(() => {
		import(
			/* webpackChunkName: "@ak-switcher-chunk-robot-error-img" */ '../../assets/robot-error'
		).then((src) => setImgSrc(src.default));
	}, []);
	return <img alt="" {...props} src={imgSrc} />;
};

const _default_1: React.ForwardRefExoticComponent<
	Omit<
		Omit<ErrorBoundaryProps & WithAnalyticsEventsProps, 'intl'> & {
			forwardedRef?: React.Ref<any>;
		},
		keyof WithAnalyticsEventsProps
	> &
		React.RefAttributes<any>
> = withAnalyticsEvents()(injectIntl(ErrorBoundary));
export default _default_1;
